import React from 'react';
import styled from 'styled-components';
import requestChangelog from './request.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { NodeConfig, NodeField, NodeFieldType, NodeOutput, NodeType, RequestNodeState } from '../../';
import { useSelector } from 'react-redux';
import { State, API_BASE, selectCommandByNodeId } from '@store/index';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { Tooltip } from '@common/Tooltip';
import { Badge, Chip, Stack } from '@mui/material';
import { useNodeError } from '@common/hooks/useNodeError';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

const Extensions = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  color: ${({ theme }) => theme.text.subtext};

  padding: 0.75rem 1rem;
  border-radius: 7px 7px 0 0;
  font-family: 'Inter';

  border-bottom: 1px solid ${({ theme }) => theme.line.node};

  & .MuiBadge-badge {
    color: white;
    height: 12px;
    min-width: 12px !important;
    max-width: 12px !important;
    font-size: 8px;
    padding: 0;
    left: -10 !important;
    margin-right: 0.25rem;
    margin-top: 0.15rem;
  }
`;

export const config: NodeConfig = {
  version: '1.0',
  title: 'Request',
  trigger: true,
  identifier: 'Request',
  command_id: 'http.request',
  background: '#00E3AA',
  pair: 'Response',
  icon: 'input',
  changelog: requestChangelog,
  anchors: [
    {
      label: 'Received',
      id: 'http.request.received',
      type: 'source' as HandleType,
      variant: 'execution',
    },
    {
      label: 'Data',
      id: 'http.request.data',
      type: 'source' as HandleType,
      variant: 'data',
      isHandle: true,
    },
  ],
};

export const state: RequestNodeState = {
  method: 'get',
  query_parameters: [],
  body_parameters: [],
};

export const fields: NodeField[] = [
  {
    id: 'method',
    type: NodeFieldType.WorkflowEndpoint,
    label: 'Endpoint',
    description: '',
    value: '',
  },
  {
    id: 'query_parameters',
    type: NodeFieldType.RequestParameter,
    label: 'Query Parameters',
    description:
      'Add query parameters to this request to use as dynamic variables in subsequent nodes. For example, you might create a query parameter called "question" for the users input to the AI Completion node.',
  },
  {
    id: 'body_parameters',
    type: NodeFieldType.RequestParameter,
    label: 'Body Parameters',
    description: 'Add body parameters to this request to use as dynamic variables in subsequent nodes.',
  },
];

export const output: NodeOutput = {
  default: {
    http: {
      request: {
        params: {
          app: 'app_name',
          flow_id: 'endpoint_name',
        },
        body: null,
        query: null,
        headers: {
          host: API_BASE,
          accept: '*/*',
          connection: 'keep-alive',
          'user-agent': 'NOC.Flow.001',
          'cache-control': 'no-cache',
          'accept-encoding': 'gzip, deflate, br',
          'content-length': '0',
        },
      },
    },
  },
  dynamic: null,
};

export const Request: React.FC<NodeType> = ({ id, selected, data }) => {
  const { background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'http.request') return;

  const nodeState: RequestNodeState = command.state;
  const method = nodeState.method;
  const queryParameters = nodeState.query_parameters;
  const bodyParameters = nodeState.body_parameters;
  const body_methods = ['post', 'put', 'patch'];

  const isToolbarVisible = Boolean(isNodeError);

  const TTQueryParameters = [
    'Query Parameters\n',
    queryParameters.map((param) => `- ${param.name}: ${param.value || 'null'}`).join('\n'),
  ].join('\n');

  const TTBodyParameters = [
    'Body Parameters\n',
    bodyParameters
      .filter((param) => param.name)
      .map((param) => (param.name ? `- ${param.name}: ${param.value || 'null'}` : null))
      .join('\n'),
  ].join('\n');

  const extensions = [
    {
      name: 'Query',
      badgeValue: queryParameters.filter((param) => param.name).length,
      tooltip: TTQueryParameters,
      visible: queryParameters.filter((param) => param.name).length,
    },
    {
      name: 'Body',
      badgeValue: bodyParameters.filter((param) => param.name).length,
      tooltip: TTBodyParameters,
      visible: body_methods.includes(method) && bodyParameters.filter((param) => param.name).length,
    },
  ];

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader
        id={id}
        title={`${nodeState.method.toUpperCase()} Request`}
        icon={icon}
        background={background}
        status={nodeStatus}
      />
      {extensions.some((item) => item.visible) && (
        <Extensions>
          {extensions.map(({ name, badgeValue, tooltip, visible }) =>
            visible ? (
              <Tooltip sx={{ maxWidth: '500px' }} key={name} title={tooltip} placement='top' arrow>
                <Badge badgeContent={badgeValue} color='primary'>
                  <Chip onClick={() => null} label={name} variant='outlined' size='small' sx={{ fontSize: '9px' }} />
                </Badge>
              </Tooltip>
            ) : null
          )}
        </Extensions>
      )}
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
